// withTimeout.ts

/**
 * Races `promise` against a `ms` timeout.
 *
 * - If `promise` settles first, its resolved value / rejection passes
 *   through untouched.
 * - If the timeout wins, `onTimeout` (if given) is called and the
 *   returned promise resolves with `undefined`. This does NOT cancel
 *   `promise` — there's no way to cancel a native Promise — it keeps
 *   running in the background and its eventual result/error is simply
 *   ignored by this call.
 *
 * Use this to keep a dependency that can occasionally hang forever
 * (never resolve *or* reject — e.g. an IndexedDB request that never
 * fires onsuccess/onerror in some browser storage contexts) from
 * blocking a caller that has a reasonable "didn't finish in time"
 * fallback.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, onTimeout?: () => void): Promise<T | undefined> {
   let timeoutId: ReturnType<typeof setTimeout>;

   const timeout = new Promise<undefined>((resolve) => {
      timeoutId = setTimeout(() => {
         onTimeout?.();
         resolve(undefined);
      }, ms);
   });

   return Promise.race([promise.finally(() => clearTimeout(timeoutId)), timeout]);
}
